import React, { useEffect } from "react";
import moment from "moment";
import { useForm } from "react-hook-form";
import { DecisionSwal } from "Utils/Utils/DecisionSwal";
import { NumberField } from "Utils/renderField/fieldsHookForms";

const ANIO_MINIMO = 2000;

const EsAnioValido = (valor) => {
    const anio = Number(valor);

    return anio >= ANIO_MINIMO && anio <= moment().year();
};

// Año y correlativo desde los que empiezan a numerarse los pallets creados en
// el sistema. Mientras no estén configurados es lo único que se puede hacer en
// el tab; en cuanto existen pallets de ese año o de años posteriores quedan
// como dato de solo lectura
export const CorrelativoInicialPallet = ({ hook }) => {
    const {
        estado,
        configurado,
        editable,
        anio,
        totalPalletsAnioElegido,
        correlativoInicial,
        CargarEstadoAnio,
        ConfigurarCorrelativo,
    } = hook;

    const {
        control: formControl,
        getValues: formGetValues,
        setValue: formSetValue,
        watch: formWatch,
        trigger: validateForm,
        formState: { errors: formErrors },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            anio: "",
            correlativo_inicial: "",
        },
    });

    const anioElegido = formWatch("anio");

    useEffect(() => {
        if (anio) {
            formSetValue("anio", anio);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anio]);

    useEffect(() => {
        if (correlativoInicial) {
            formSetValue("correlativo_inicial", correlativoInicial);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [correlativoInicial]);

    // el año que ya muestra el estado general no se vuelve a consultar
    useEffect(() => {
        const anioConsulta =
            EsAnioValido(anioElegido) && Number(anioElegido) !== anio
                ? Number(anioElegido)
                : null;

        CargarEstadoAnio(anioConsulta);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anioElegido, anio]);

    const anioTexto = EsAnioValido(anioElegido) ? anioElegido : anio;

    const ConfirmarGuardado = async () => {
        const valid = await validateForm();
        if (!valid) return;

        const { anio: anioGuardar, correlativo_inicial: correlativo } =
            formGetValues();

        let mensaje = "Los pallets que se creen en el sistema empezarán a ";
        mensaje += `numerarse desde el ${correlativo}-${anioGuardar}. `;

        if (totalPalletsAnioElegido) {
            mensaje += `Los ${totalPalletsAnioElegido} pallets de ${anioGuardar} `;
            mensaje += "que ya existen en el sistema se renumerarán a partir ";
            mensaje += "de ese número. ";
        }

        mensaje += "¿Deseas continuar?";

        DecisionSwal(mensaje, "Sí, guardar", "Cancelar", () =>
            ConfigurarCorrelativo(Number(anioGuardar), correlativo),
        );
    };

    if (!estado) return null;

    if (configurado && !editable) {
        return (
            <div className="inventario-inicial__correlativo">
                {correlativoInicial && (
                    <span>
                        Los pallets del sistema se numeran desde el{" "}
                        <b>
                            {correlativoInicial}-{anio}
                        </b>
                        .
                    </span>
                )}
            </div>
        );
    }

    return (
        <React.Fragment>
            {!configurado && (
                <div className="inventario-inicial__ayuda">
                    <p className="mb-1">
                        Antes de registrar pallets en el sistema, indique{" "}
                        <b>
                            el año y el correlativo desde los que se deben
                            numerar los pallets.{" "}
                        </b>
                        Los números de pallet no se pueden repetir, por lo que
                        es necesario continuar desde el último correlativo
                        utilizado en ese año. Si va a registrar datos de un año
                        anterior, indique ese año; los años siguientes se
                        numeran desde 1.
                    </p>
                    <p className="mb-0">
                        Ejemplo: si el último pallet creado físicamente es el
                        425-{anioTexto}, debe ingresar 426 como correlativo
                        inicial. Si todavía no se ha creado ningún pallet en ese
                        año, ingrese 1.
                    </p>
                </div>
            )}

            {totalPalletsAnioElegido > 0 && (
                <div className="inventario-inicial__bloqueado">
                    Ya hay {totalPalletsAnioElegido} pallets de {anioTexto}{" "}
                    creados en el sistema. Al guardar se renumerarán a partir
                    del correlativo que indique, conservando su orden.
                </div>
            )}

            <div className="formulario-fields-row align-items-start mt-2">
                <div className="formulario-field min-w-150">
                    <label className="text-nowrap">Año</label>
                    <NumberField
                        control={formControl}
                        name="anio"
                        errors={formErrors}
                        allowNegative={false}
                        thousandSeparator={false}
                        decimalScale={0}
                        rules={{
                            required: "Debe indicar el año",
                            validate: (valor) => {
                                if (Number(valor) > moment().year()) {
                                    return "El año no puede ser posterior al año actual";
                                }

                                if (Number(valor) < ANIO_MINIMO) {
                                    return "El año no es válido";
                                }

                                return true;
                            },
                        }}
                    />
                </div>

                <div className="formulario-field min-w-150">
                    <label className="text-nowrap">Correlativo inicial</label>
                    <NumberField
                        control={formControl}
                        name="correlativo_inicial"
                        errors={formErrors}
                        allowNegative={false}
                        thousandSeparator={false}
                        decimalScale={0}
                        rules={{
                            required: "Debe indicar el correlativo inicial",
                            min: {
                                value: 1,
                                message:
                                    "El correlativo inicial debe ser mayor a cero",
                            },
                        }}
                    />
                </div>

                <div className="formulario-field min-w-150 no-flex">
                    <button
                        className="btn btn-primary"
                        onClick={ConfirmarGuardado}
                        style={{ marginTop: "1.6rem" }}
                    >
                        Guardar
                    </button>
                </div>
            </div>
        </React.Fragment>
    );
};
